import React, { useEffect, useRef } from 'react';
import { Range, getTrackBackground } from 'react-range';
import { Button, Drawer } from '../../../../../ui';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import { StatusToggle } from '../../Interviews/Containers/Candidates/Components';
import ColorRangeSelector, { ColorRangeSelectorRef } from '../UI/ColorRangeSelector';

type Analytic3DProps = {
    isOpen: boolean,
    onCloseDrawer: () => void,
    scatter: boolean,
    setScatter: (value: boolean) => void,
    view3D: boolean,
    setView3D: (value: boolean) => void,
    markLines: boolean,
    setMarkLines: (value: boolean) => void,
    nineBox: boolean,
    setNineBox: (value: boolean) => void,
    setSimilarKeyword: React.Dispatch<React.SetStateAction<string>>,
    similarKeyword: string,
    setExactKeyword: React.Dispatch<React.SetStateAction<string>>,
    exactKeyword: string,
    setKeywordList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    colorList: string[],
    setColorList: (value: (((prevState: string[]) => string[]) | string[])) => void,
    useSkillPercentage: boolean,
    setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setExperienceValue: (value: number[]) => void,
    skillValue: number[],
    setSkillValue: (value: number[]) => void,
    experienceValue: number[],
    expTitle: string,
    skillTitle: string,
    setSvgActiveTab: (value: (((prevState: number) => number) | number)) => void,
    projection: string,
    setProjection: (value: (((prevState: string) => string) | string)) => void,
    boxWidthDepth: number[],
    setBoxWidthDepth: (value: (((prevState: number[]) => number[]) | number[])) => void
};

export const Analytic3D = ({
  isOpen,
  onCloseDrawer,
  scatter,
  setScatter,
  view3D,
  setView3D,
  markLines,
  setMarkLines,
  nineBox,
  setNineBox,
  setSimilarKeyword,
  similarKeyword,
  setExactKeyword,
  exactKeyword,
  setKeywordList,
  colorList,
  setColorList,
  useSkillPercentage,
  setUseSkillPercentage,
  setExperienceValue,
  skillValue,
  setSkillValue,
  experienceValue,
  expTitle,
  skillTitle,
  setSvgActiveTab,
  projection,
  setProjection,
  boxWidthDepth,
  setBoxWidthDepth,
}: Analytic3DProps) => {

  const colorSelectorRef = useRef<ColorRangeSelectorRef>(null);

  useEffect(() => {
    const similarList = similarKeyword.split(':').filter(Boolean);
    const exactList = exactKeyword.split(':').filter(Boolean);
    const mergedList = Array.from(new Set([...similarList, ...exactList]));
    setKeywordList(mergedList);
  }, [similarKeyword, exactKeyword]);

  const convertStringToObjects = (
    input: string,
  ): { label: string; value: string }[] => {
    if (input === '') return [];
    return input.split(':').map((item) => ({
      label: item,
      value: item,
    }));
  };

  const handleApply = () => {
    const newColors = colorSelectorRef.current?.getColorList();
    if (newColors) {
      setColorList(newColors);
    }
    onCloseDrawer();
  };

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        footerContent={
          <div className='flex w-full justify-end gap-2'>
            <Button label='Apply' onClick={handleApply} variant='primary' />
            <Button label='Close' onClick={onCloseDrawer} variant='secondary' />
          </div>
        }
        title='Graph Configurations'
        width='w-full md:w-[560px] xl:w-[620px]'
    >
      <div className='space-y-2.5'>
        { /* Section: General Settings - compact modern card */ }
        <section className='rounded-lg border border-default-200 bg-white p-2.5 shadow-sm'>
          <div className='mb-2 flex items-center justify-between'>
            <h2 className='text-[13px] font-semibold tracking-tight text-foreground'>General Settings</h2>
            <span className='rounded-full bg-default-100 px-2 py-0.5 text-[10px] font-medium text-default-500'>2D / 3D</span>
          </div>

          <div className='flex flex-col gap-2.5'>
            { /* Skill View Mode - HeroUI compact */ }
            <div className='flex items-center justify-between rounded-md bg-default-50 px-2.5 py-2'>
              <label
                  htmlFor='skill-view-toggle'
                  className='text-xs font-medium text-default-700 cursor-pointer select-none'
              >
                Skill View Mode
              </label>
              <div id='skill-view-toggle' className='scale-[0.9] origin-right'>
                <StatusToggle
                    options={['Skill Count', 'Skill Percentage']}
                    value={
                      useSkillPercentage ? 'Skill Percentage' : 'Skill Count'
                    }
                    onChange={(val) =>
                      setUseSkillPercentage(val === 'Skill Percentage')
                    }
                />
              </div>
            </div>

            { /* Toggles - compact grid */ }
            <div className='grid grid-cols-2 gap-2'>
              <div className='rounded-md border border-default-200 px-2 py-1.5'>
                <ToggleInput
                    toggled={nineBox}
                    handleToggle={() => setNineBox(!nineBox)}
                    name='nineBox'
                    informationText='9 Box Grid Overlay'
                />
              </div>
              <div className='rounded-md border border-default-200 px-2 py-1.5'>
                <ToggleInput
                    toggled={markLines}
                    handleToggle={() => setMarkLines(!markLines)}
                    name='markLines'
                    informationText='Guideline Grid'
                    disabled={!nineBox}
                />
              </div>
            </div>

            { /* Sliders - compact */ }
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <output className='mb-1 block text-xs font-medium text-default-700'>
                  { skillTitle } <span className='font-normal text-default-500'>({ skillValue[0] }–{ skillValue[1] })</span>
                </output>
                <div className='px-1.5'>
                  <Range
                      values={skillValue}
                      step={1}
                      min={0}
                      max={100}
                      onChange={(newValues) => setSkillValue(newValues)}
                      renderTrack={({ props, children }) => (
                        <div
                            {...props}
                            className='h-1.5 w-full rounded-full'
                            style={{
                              ...props.style,
                              background: getTrackBackground({
                                values: skillValue,
                                colors: ['#e5e7eb', '#3b82f6', '#e5e7eb'],
                                min: 0,
                                max: 100,
                              }),
                            }}
                        >
                          { children }
                        </div>
                      )}
                      renderThumb={({ props, isDragged }) => (
                        <div
                            {...props}
                            className='h-4 w-4 rounded-full bg-white flex justify-center items-center shadow-sm ring-1 ring-default-300'
                            style={props.style}
                        >
                          <div className={`h-2.5 w-0.5 rounded-sm ${isDragged ? 'bg-primary' : 'bg-default-400'}`} />
                        </div>
                      )}
                  />
                </div>
              </div>

              <div>
                <output className='mb-1 block text-xs font-medium text-default-700'>
                  { expTitle } <span className='font-normal text-default-500'>({ experienceValue[0] }–{ experienceValue[1] })</span>
                </output>
                <div className='px-1.5'>
                  <Range
                      values={experienceValue}
                      step={1}
                      min={0}
                      max={100}
                      onChange={(newValues) => setExperienceValue(newValues)}
                      renderTrack={({ props, children }) => (
                        <div
                            {...props}
                            className='h-1.5 w-full rounded-full'
                            style={{
                              ...props.style,
                              background: getTrackBackground({
                                values: experienceValue,
                                colors: ['#e5e7eb', '#3b82f6', '#e5e7eb'],
                                min: 0,
                                max: 100,
                              }),
                            }}
                        >
                          { children }
                        </div>
                      )}
                      renderThumb={({ props, isDragged }) => (
                        <div
                            {...props}
                            className='h-4 w-4 rounded-full bg-white flex justify-center items-center shadow-sm ring-1 ring-default-300'
                            style={props.style}
                        >
                          <div className={`h-2.5 w-0.5 rounded-sm ${isDragged ? 'bg-primary' : 'bg-default-400'}`} />
                        </div>
                      )}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        { /* Compact divider */ }
        <div className='flex items-center gap-2 py-0.5'>
          <div className='h-px flex-1 bg-default-200' />
          <span className='rounded-full border border-default-200 bg-white px-2 py-0.5 text-[10px] font-semibold tracking-wider text-default-500'>3D GRAPH</span>
          <div className='h-px flex-1 bg-default-200' />
        </div>

        { /* Section: 3D View Settings - compact */ }
        <section className='rounded-lg border border-default-200 bg-white p-2.5 shadow-sm'>
          <h2 className='mb-2 text-[13px] font-semibold tracking-tight text-foreground'>
            3D View Settings
          </h2>

          <div className='flex flex-col gap-2.5'>
            <div className='grid grid-cols-2 gap-2'>
              <div className='rounded-md border border-default-200 px-2 py-1.5'>
                <ToggleInput
                    toggled={view3D}
                    handleToggle={() => {
                      setView3D(!view3D);
                      setSvgActiveTab(-1);
                    }}
                    name='view3D'
                    informationText='Enable 3D View'
                />
              </div>
              <div className='rounded-md border border-default-200 px-2 py-1.5'>
                <ToggleInput
                    toggled={scatter}
                    handleToggle={() => setScatter(!scatter)}
                    name='scatter'
                    informationText='Scatter vs Bar'
                    disabled={!view3D}
                />
              </div>
            </div>

            <div className='grid grid-cols-[110px_1fr] gap-3'>
              <div>
                <label className='mb-1.5 block text-xs font-medium text-default-700'>
                  Keyword Color
                </label>
                <div className='rounded-md border border-default-200 bg-default-50 p-1.5'>
                  <ColorRangeSelector ref={colorSelectorRef} />
                </div>
              </div>

              <div className='min-w-0'>
                <label className='mb-1.5 block text-xs font-medium text-default-700'>
                  Advanced
                </label>
                <div className='space-y-2.5'>
                  <div>
                    <output className='mb-1 block text-xs font-medium text-default-700'>
                      Box Width <span className='text-default-500'>({ boxWidthDepth[0] })</span>
                    </output>
                    <div className='px-1.5'>
                      <Range
                          values={[boxWidthDepth[0]]}
                          step={1}
                          min={0}
                          max={500}
                          onChange={([val]) => setBoxWidthDepth(([_, d]) => [val, d])}
                          renderTrack={({ props, children }) => (
                            <div {...props} className='h-1.5 w-full rounded-full' style={{ ...props.style, background: getTrackBackground({ values: [boxWidthDepth[0]], colors: ['#3b82f6', '#e5e7eb'], min: 0, max: 500 })} }>{ children }</div>
                          )}
                          renderThumb={({ props, isDragged }) => (
                            <div {...props} className='h-4 w-4 rounded-full bg-white flex justify-center items-center shadow-sm ring-1 ring-default-300' style={props.style}><div className={`h-2.5 w-0.5 rounded-sm ${isDragged ? 'bg-primary' : 'bg-default-400'}`} /></div>
                          )}
                      />
                    </div>
                  </div>
                  <div>
                    <output className='mb-1 block text-xs font-medium text-default-700'>
                      Box Depth <span className='text-default-500'>({ boxWidthDepth[1] })</span>
                    </output>
                    <div className='px-1.5'>
                      <Range
                          values={[boxWidthDepth[1]]}
                          step={1}
                          min={0}
                          max={500}
                          onChange={([val]) => setBoxWidthDepth(([w, _]) => [w, val])}
                          renderTrack={({ props, children }) => (
                            <div {...props} className='h-1.5 w-full rounded-full' style={{ ...props.style, background: getTrackBackground({ values: [boxWidthDepth[1]], colors: ['#3b82f6', '#e5e7eb'], min: 0, max: 500 })} }>{ children }</div>
                          )}
                          renderThumb={({ props, isDragged }) => (
                            <div {...props} className='h-4 w-4 rounded-full bg-white flex justify-center items-center shadow-sm ring-1 ring-default-300' style={props.style}><div className={`h-2.5 w-0.5 rounded-sm ${isDragged ? 'bg-primary' : 'bg-default-400'}`} /></div>
                          )}
                      />
                    </div>
                  </div>
                  <div>
                    <output className='mb-1 block text-xs font-medium text-default-700'>Projection</output>
                    <div className='scale-[0.9] origin-left'>
                      <StatusToggle
                          options={['perspective', 'orthographic']}
                          value={projection === 'perspective' ? 'perspective' : 'orthographic'}
                          onChange={(val) => setProjection(val)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Drawer>
  );

};
